import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';

// --- Types ---
interface QuestionData {
    index: number;
    qn_no: string;
    content: string;
    type: string;
    complexity: string;
    used_in_t: string;
}

interface XmlStats {
    NoOfEasyQns_Exe: number;
    NoOfMedQns_Exe: number;
    NoOfDiffQns_Exe: number;
    AdvancedQnNos_All: string;
    NoOfEasyQns_GEN_Exe: number;
    NoOfMedQns_GEN_Exe: number;
    NoOfDiffQns_GEN_Exe: number;
    EasyQnNos_All: string;
    MedQnNos_All: string;
    DiffQnNos_All: string;
}

interface LessonData {
    lesson_name: string;
    questions: QuestionData[];
    tutorials: string;
    principles: string;
    xml_stats: XmlStats;
    usage_list: string[];
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const LessonViewer: React.FC = () => {

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [data, setData] = useState<LessonData | null>(null);
    const [loading, setLoading] = useState(true);

    const dicFile = searchParams.get('Dicfilename') || '';
    const country = searchParams.get('Country') || 'Australia';
    const lessonId = searchParams.get('Lessonid') || '';
    const mdcode = searchParams.get('mdcode') || '';
    const eccode = searchParams.get('eccode') || '';
    const from = searchParams.get('from') || '';

    const codeParam = mdcode
        ? `&mdcode=${mdcode}`
        : eccode
            ? `&eccode=${eccode}`
            : '';

    const handleBack = () => {
        const calledfrom = sessionStorage.getItem('Calledfrom') || '';
        const f = sessionStorage.getItem('Calledfromf') || '';
        const l = sessionStorage.getItem('Calledfroml') || '';

        if (calledfrom && calledfrom !== 'LessonBrowser') {
            // Mirrors VB: parse the query string part after '?'
            const parts = calledfrom.split('?');
            if (parts.length === 2) {
                const params = new URLSearchParams(parts[1]);
                const s = params.get('s') || '';
                navigate(`/SUPPORT/EL_ShowCurrentActivities?s=${s}&f=${f}&l=${l}`);
                return;
            }
        }

        navigate(
            `/LessonBrowser/LessonBrowser?Country=${country}` +
            `&Dicfilename=${encodeURIComponent(dicFile)}` +
            `${codeParam}`
        );
    };

    useEffect(() => {
        document.body.style.backgroundColor = '#fff';
    }, []);


    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'lesson-viewer-override';
        style.textContent = `
        .question-content p {
            color: rgb(0 0 255) !important;
            display: block !important;
            margin-top: 0 !important;
            margin-left: 0 !important;
            font-size: 16px;
            padding: 10px;
        }
    .question-content p:empty,
    .principles-content p:empty {
        display: none !important;
    }

    .question-content {
        max-width: 100%;
        overflow-x: auto;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }
    .question-content table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed !important;
    }
    .question-content img {
        max-width: 100% !important;
        height: auto !important;
    }

               .principles-content p {
        color: #000000 !important;
        display: block !important;
        margin-top: 0 !important;
        margin-left: 0 !important;
        font-weight: normal !important;
          font-size: 16px;
            padding: 10px;
    }
    `;
        document.head.appendChild(style);
        return () => {
            document.getElementById('lesson-viewer-override')?.remove();
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!lessonId) return;
            setLoading(true);
            try {
                const url = `${API_BASE_URL}api/lesson_viewer_get_data?lesson_id=${lessonId}&country=${country}&mdcode=${mdcode}&eccode=${eccode}`;
                const res = await fetch(url);
                const json = await res.json();
                if (json.error) {
                    alert(json.error);
                } else {
                    setData(json);
                    sessionStorage.setItem(
                        "lessonQuestions",
                        JSON.stringify(json.questions)
                    );
                }
            } catch (err) {
                console.error('Fetch Error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [lessonId, country, mdcode, eccode]);


    const stripInlineEvents = (html: string | undefined): string => {
        if (!html) return '';
        return html.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*')/gi, '');
    };

    const cleanLegacyHtml = (html: string | undefined): string => {
        if (!html) return '';
        return html.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*')/gi, '');
    };

    const countItems = (str: string): number => {
        if (!str) return 0;
        return (str.match(/.{1,3}/g) || []).length;
    };

    const fmtStat = (val: string | number): string => {
        const s = String(val);
        if (!s || s === '0' || s === '0 / 0') return '';
        if (/^\d+ \/ 0$/.test(s)) return '';
        return s;
    };

    const buildTutorialPreview = (rawHtml: string): string => {
        if (!rawHtml) return '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, 'text/html');
        const body = doc.body;

        body.querySelectorAll('center').forEach(el => el.remove());
        // Also remove script/style tags that may be in the body
        body.querySelectorAll('script, style').forEach(el => el.remove());

        let textCount = 0;
        let truncatedHTML = '';

        const processNode = (node: Node) => {
            if (textCount >= 130) return;

            if (node.nodeType === Node.TEXT_NODE) {
                const text = (node.textContent || '').replace(/\s+/g, ' ');
                for (const ch of text) {
                    if (textCount >= 130) break;
                    truncatedHTML += ch;
                    if (ch.trim() !== '') textCount++;
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as Element;
                if (el.tagName === 'IMG') {
                    const src = el.getAttribute('src');
                    if (src) {
                        truncatedHTML += `<img src="${src}" style="max-height:50px;max-width:50px;vertical-align:middle;">`;
                    }
                } else {
                    // Recurse into all other elements (not just top-level)
                    node.childNodes.forEach(child => processNode(child));
                }
            }
        };

        // Walk all children of body
        body.childNodes.forEach(child => processNode(child));
        return truncatedHTML;
    };

    const styles = {
        navigator: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            backgroundColor: '#7a96df', height: '50px', padding: '0 10px',
            color: 'white', fontSize: '18px', fontFamily: 'Arial'
        } as React.CSSProperties,
        navLink: {
            color: 'white', textDecoration: 'none', fontWeight: 'bold' as const,
            fontFamily: 'Arial', marginLeft: '10px', cursor: 'pointer'
        } as React.CSSProperties,
        headMsg: {
            padding: '2px', width: '100%', fontSize: '16pt', margin: '5px',
            color: 'maroon', fontFamily: 'Times New Roman', fontWeight: 'normal' as const,
        } as React.CSSProperties,
        qTable: {
            borderCollapse: 'collapse' as const, width: '100%',
            border: '1px solid #eeeeee', fontFamily: 'Times New Roman', fontSize: '16px',
        } as React.CSSProperties,
        qTh: {
            border: '1px solid #eeeeee', padding: '4px 2px',
            textAlign: 'left' as const, background: '#ffffff', fontFamily: 'Times New Roman'
        } as React.CSSProperties,
        qTd: {
            border: '1px solid #eeeeee', padding: '4px 2px',
            textAlign: 'left' as const, verticalAlign: 'middle' as const,
            color: '#000000', fontFamily: 'Times New Roman', fontSize: "14px"  // ← add this
        } as React.CSSProperties,
        sideTable: {
            borderCollapse: 'collapse' as const, width: '100%',
            border: '1px solid #eeeeee', fontFamily: 'Times New Roman', fontSize: '14px', marginBottom: '10px',
        } as React.CSSProperties,
        sideTh: {
            border: '1px solid #eee', padding: '5px', borderBottom: '1px solid #eee',
            textAlign: 'center' as const, fontWeight: 'bold' as const, fontFamily: 'Times New Roman'
        } as React.CSSProperties,
        sideTd: {
            border: '1px solid #eee', padding: '5px', textAlign: 'center' as const, fontFamily: 'Times New Roman'
        } as React.CSSProperties,
        btn: {
            marginLeft: '10px', color: 'white', backgroundColor: 'transparent',
            fontSize: '18px', border: 'none', cursor: 'pointer', fontFamily: 'Times New Roman'
        } as React.CSSProperties
    };

    const renderQuestions = () => {
        if (!data?.questions) return null;

        return (
            <table style={styles.qTable}>
                <thead>
                    <tr>
                        <th style={{ ...styles.qTh, width: '92px', textAlign: 'center' }}>#</th>
                        <th style={{ ...styles.qTh, width: '15px', textAlign: 'center' }}></th>
                        <th style={{ ...styles.qTh, width: '15px', textAlign: 'center' }}></th>
                        <th style={{ ...styles.qTh, width: '15px', textAlign: 'center' }}></th>
                        <th style={{ ...styles.qTh, width: '15px', textAlign: 'center' }}></th>
                        <th style={{ ...styles.qTh, textAlign: 'center' }}>Questions</th>
                    </tr>
                </thead>
                <tbody>
                    {data.questions.map((q) => {
                        let bg = '';
                        let compShort = '';
                        if (q.complexity === 'Easy') { bg = 'lightgreen'; compShort = 'E'; }
                        else if (q.complexity === 'Medium') { bg = 'yellow'; compShort = 'M'; }
                        else if (q.complexity === 'Hard') { bg = 'lightcoral'; compShort = 'D'; }

                        const qViewerPath =
                            `/LessonBrowser/QuestionViewer?Qnno=${q.qn_no}${codeParam}&Lessonid=${lessonId}&Country=${country}`;

                        return (
                            <tr key={q.qn_no}>
                                <td style={{ ...styles.qTd, width: '95px' }}>
                                    <Link
                                        to={qViewerPath}
                                        style={{ textDecoration: 'underline', color: 'blue' }}
                                        onClick={() => sessionStorage.setItem('currentIndex', String(q.index - 1))}
                                    >
                                        <b>Question {q.qn_no}</b>
                                    </Link>
                                </td>
                                <td style={{ ...styles.qTd, textAlign: 'center', width: '15px' }}>{q.type}</td>
                                <td style={{ ...styles.qTd, textAlign: 'center', width: '15px', backgroundColor: bg }}>{compShort}</td>
                                {/* VB always shows 'L' in the 4th narrow col */}
                                <td style={{ ...styles.qTd, textAlign: 'center', width: '15px' }}>L</td>
                                <td style={{ ...styles.qTd, textAlign: 'center', width: '15px' }}>{q.used_in_t}</td>
                                <td style={{ ...styles.qTd, maxWidth: '400px' }}>
                                    <div
                                        className="question-content"
                                        dangerouslySetInnerHTML={{
                                            __html: stripInlineEvents(q.content)

                                                .replace(/<p[^>]*>/gi, '<p style="font-family:Arial; font-weight:normal; margin:0; display:block; font-size:16px">')
                                                .replace(/<b>/gi, '<b style="font-weight:normal;">')
                                                .replace(/<strong>/gi, '<strong style="font-weight:normal;">')
                                        }}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const renderTutorials = () => {
        const tutLink = `/LessonBrowser/Tutorial_PrincipleContents?Lessonid=${lessonId}&Country=${country}&tp=tut${codeParam}`;

        const preview = buildTutorialPreview(data?.tutorials || '');

        return (
            <table style={{ ...styles.sideTable, width: '100%' }}>
                <thead>
                    <tr style={{ backgroundColor: '#fff' }}>
                        <th style={styles.sideTh}>
                            <a href={tutLink} style={{ color: 'blue' }}>Tutorials</a>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {/* VB: width="400" in td; text-align left */}
                        <td style={{ ...styles.sideTd, width: '400px', textAlign: 'left' }}>
                            {preview
                                ? <div dangerouslySetInnerHTML={{ __html: preview }} />
                                : <span>No Tutorial Data</span>
                            }
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    };

    const renderPrinciples = () => {
        if (!data?.principles) return null;
        const parts = data.principles.split('|').filter(p => p.trim() !== '');
        if (parts.length === 0) return null;

        return (
            <table style={{ ...styles.sideTable, width: '100%', marginTop: '10px' }}>
                <thead>
                    <tr>
                        {/* VB JS: single header cell spanning 2 cols "Principles" */}
                        <th colSpan={2} style={{ ...styles.sideTh, borderBottom: '1px solid #000' }}>Principles</th>
                    </tr>
                </thead>
                <tbody>
                    {parts.map((part, idx) => {
                        // Extract 3-digit number for the link parameter (tp=Prc_NNN)
                        const matchTitle3 = part.match(/<title>.*?(\d{3})<\/title>/i);
                        // Extract 2-digit number for the display text (PrcNN)
                        const matchTitle2 = part.match(/<title>.*?(\d{2})<\/title>/i);
                        const lastThree = matchTitle3 ? matchTitle3[1] : '000';
                        const lastTwo = matchTitle2 ? matchTitle2[1] : '00';

                        const prcLink = `/LessonBrowser/Tutorial_PrincipleContents?Lessonid=${lessonId}&Country=${country}&tp=Prc_${lastThree}${codeParam}`;

                        return (
                            <tr key={idx}>
                                <td style={{ ...styles.sideTd, width: '50px', border: "1px solid #000" }}>
                                    <a href={prcLink} style={{ color: 'blue' }}>Prc{lastTwo}</a>
                                </td>
                                <td style={{ ...styles.sideTd, textAlign: 'left', border: "1px solid #000" }}>
                                    <div
                                        className="principles-content"
                                        dangerouslySetInnerHTML={{ __html: cleanLegacyHtml(part) }}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const renderAssignedQuestions = () => {
        if (!data?.xml_stats) return null;
        const s = data.xml_stats;

        const totalEasy = countItems(s.EasyQnNos_All);
        const totalMed = countItems(s.MedQnNos_All);
        const totalDiff = countItems(s.DiffQnNos_All);

        const sumGen = s.NoOfEasyQns_GEN_Exe + s.NoOfMedQns_GEN_Exe + s.NoOfDiffQns_GEN_Exe;
        const sumTotal = totalEasy + totalMed + totalDiff;
        const sumFixed = s.NoOfEasyQns_Exe + s.NoOfMedQns_Exe + s.NoOfDiffQns_Exe;

        const rows = [
            {
                type: 'Easy',
                fixed: `${s.NoOfEasyQns_Exe} / ${totalEasy}`,
                generated: `${s.NoOfEasyQns_GEN_Exe} / ${totalEasy}`
            },
            {
                type: 'Medium',
                fixed: `${s.NoOfMedQns_Exe} / ${totalMed}`,
                generated: `${s.NoOfMedQns_GEN_Exe} / ${totalMed}`
            },
            {
                type: 'Difficult',
                fixed: `${s.NoOfDiffQns_Exe} / ${totalDiff}`,
                generated: `${s.NoOfDiffQns_GEN_Exe} / ${totalDiff}`
            },
            // VB JS: Advanced row shows raw AdvancedQnNos_All string in both cols
            {
                type: 'Advanced',
                fixed: s.AdvancedQnNos_All,
                generated: s.AdvancedQnNos_All
            }
        ];

        return (
            <div>
                {/* VB JS: <h4>Assigned Questions</h4> before the table */}
                <h4 style={{ marginBottom: '10px', fontFamily: 'Times New Roman' }}>Assigned Questions</h4>
                <table style={{ ...styles.sideTable, width: '350px' }}>
                    <thead>
                        <tr style={{ backgroundColor: 'rgb(240, 240, 240)' }}>
                            <th style={styles.sideTh}>Type</th>
                            <th style={styles.sideTh}>Fixed</th>
                            <th style={styles.sideTh}>Generated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td style={styles.sideTd}>{r.type}</td>
                                {/* fmtStat mirrors VB JS: (val===0||val==="0") ? "" : val */}
                                <td style={styles.sideTd}>{(r.fixed)}</td>
                                <td style={styles.sideTd}>{(r.generated)}</td>
                            </tr>
                        ))}
                        {/* Total row — VB JS: bold, "sumFixed / sumTotal" and sumGenerated (blank if 0) */}
                        <tr>
                            <td style={{ ...styles.sideTd, fontWeight: 'bold' }}>Total Questions</td>
                            <td style={{ ...styles.sideTd, fontWeight: 'bold' }}>
                                {sumFixed} / {sumTotal}
                            </td>
                            <td style={{ ...styles.sideTd, fontWeight: 'bold' }}>
                                {sumGen === 0 ? '' : sumGen}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderUsage = () => {
        if (!data?.usage_list || data.usage_list.length === 0) return null;

        return (
            <div style={{ marginTop: '10px' }}>
                <h4 style={{ fontFamily: 'Times New Roman', marginBottom: '5px' }}>
                    Usage (by dictionaries in {country})
                </h4>
                <ul style={{ paddingLeft: '20px', marginTop: '5px' }}>
                    {data.usage_list.map((u, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>
                            <a
                                href={`/LessonBrowser/LessonBrowser?Country=${country}&Dicfilename=${u}&Lessonid=${lessonId}&FromUsage=1`}
                                style={{ fontFamily: 'Times New Roman', color: '#0073e6', textDecoration: 'underline' }}
                            >
                                {u}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };


    if (loading) return <Loading />;

    return (
        <div style={{ overflowX: 'hidden', padding: '8px' }}>

            <div style={styles.navigator}>
                {from !== 'SearchQnTypes' ? (
                    <button style={styles.btn} onClick={handleBack}>
                        Back
                    </button>
                ) : (
                    <div style={{ width: '60px' }} />
                )}

                <div style={{ textAlign: 'center', flexGrow: 1, fontSize: '24px' }}>
                    Lesson Navigator
                </div>

                <a style={styles.navLink} href="/">
                    Logout
                </a>
            </div>

            <p style={styles.headMsg}>
                <span dangerouslySetInnerHTML={{ __html: data?.lesson_name || '' }} />
                <span style={{ marginLeft: '104px' }}>
                    <a
                        style={{ fontSize: '11pt', color: 'maroon', display: 'none', fontFamily: 'Times New Roman' }}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Whiteboard
                    </a>
                </span>
            </p>

            {/* Two-column layout — VB JS: display:flex; gap:20px; left 60%, right 40% */}
            <div style={{ display: 'flex', gap: '20px', padding: '10px' }}>
                <div style={{ width: '60%' }}>
                    {renderQuestions()}
                </div>
                <div style={{ width: '40%' }}>
                    {renderTutorials()}
                    {renderPrinciples()}
                    {renderAssignedQuestions()}
                    {renderUsage()}
                </div>
            </div>

        </div>
    );
};

export default LessonViewer;